if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        window.setTimeout(() => this.filterVariantMedia(), 0);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            const productInfo = this.closest('product-info');
            if (!productInfo || data?.sectionId !== productInfo.sectionId) return;
            this.filterVariantMedia();
          });
        }

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.bindThumbnailEvents();
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      bindThumbnailEvents() {
        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          if (mediaToSwitch.dataset.thumbnailBound === 'true') return;

          mediaToSwitch.dataset.thumbnailBound = 'true';
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
      }

      onSlideChanged(event) {
        const mediaId = event.detail.currentElement?.dataset.mediaId;
        if (!mediaId) return;

        const activeMedia = this.elements.viewer.querySelector('[data-media-id="' + mediaId + '"]');
        if (!activeMedia) return;

        this.elements.viewer.querySelectorAll('[id^="Slider-Gallery"] > [data-media-id]').forEach((element) => {
          element.classList.toggle('is-active', element === activeMedia);
        });

        this.playActiveMedia(activeMedia);

        const thumbnail = this.elements.thumbnails.querySelector('[data-target="' + mediaId + '"]');
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[id^="Slider-Gallery"] > [data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();

        const deferredMedia = activeItem.matches('deferred-media')
          ? activeItem
          : activeItem.querySelector('deferred-media, .deferred-media');

        if (!deferredMedia || typeof deferredMedia.loadContent !== 'function') return;

        deferredMedia.classList.remove('variant-media-hidden');
        deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      normalizeMediaToken(value) {
        return String(value || '')
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-z0-9]/g, '');
      }

      getSelectedColor(productInfo) {
        const swatchFieldsets = productInfo.querySelectorAll('.product-form__input--swatch, .product-form__input--pill');

        for (const fieldset of swatchFieldsets) {
          const legend = fieldset.querySelector('.form__label');
          if (!/colou?r/i.test(legend?.textContent || '')) continue;

          const selectedValue = legend.querySelector('[data-selected-value]')?.textContent?.trim();
          if (selectedValue) return selectedValue;

          const checkedInput = fieldset.querySelector('input:checked');
          if (checkedInput?.value) return checkedInput.value;
        }

        const dropdownLabels = productInfo.querySelectorAll('.product-form__input--dropdown .form__label');
        for (const label of dropdownLabels) {
          if (!/colou?r/i.test(label.textContent || '')) continue;
          const select = label.parentElement?.querySelector('select');
          if (select?.value) return select.value;
        }

        return '';
      }

      getSelectedVariant(productInfo) {
        const variantJson = productInfo.querySelector('variant-selects [data-selected-variant]')?.textContent;
        if (!variantJson) return null;

        try {
          return JSON.parse(variantJson);
        } catch {
          return null;
        }
      }

      getMediaPosition(item, index) {
        const position = Number(item.dataset.mediaPosition);
        return Number.isFinite(position) && position > 0 ? position : index + 1;
      }

      getOrderedMediaItems(mediaItems) {
        return mediaItems
          .map((item, index) => ({
            item,
            index,
            position: this.getMediaPosition(item, index),
          }))
          .sort((first, second) => first.position - second.position || first.index - second.index)
          .map(({ item }) => item);
      }

      filterVariantMedia() {
        const shouldFilter = this.dataset.hideVariantMedia === 'true' || this.dataset.filterVariantMedia === 'true';
        if (!shouldFilter) return;

        const productInfo = this.closest('product-info');
        if (!productInfo) return;

        const selectedColorToken = this.normalizeMediaToken(this.getSelectedColor(productInfo));
        const selectedVariant = this.getSelectedVariant(productInfo);
        const selectedMediaId = selectedVariant?.featured_media?.id ? String(selectedVariant.featured_media.id) : '';
        const mediaItems = Array.from(this.querySelectorAll('[id^="Slider-Gallery"] > [data-media-id]'));
        const thumbnailItems = Array.from(this.querySelectorAll('[id^="Slider-Thumbnails"] > [data-target]'));

        this.querySelectorAll('[id^="Slider-Gallery"] [data-media-id].variant-media-hidden').forEach((item) => {
          if (!mediaItems.includes(item)) item.classList.remove('variant-media-hidden');
        });
        if (!mediaItems.length) return;

        const orderedMediaItems = this.getOrderedMediaItems(mediaItems);
        const selectedMediaItem = selectedMediaId
          ? orderedMediaItems.find((item) => item.dataset.mediaId?.endsWith(`-${selectedMediaId}`))
          : orderedMediaItems.find((item) => item.classList.contains('is-active'));
        const selectedMediaDataId = selectedMediaItem?.dataset.mediaId || '';
        const selectedMediaIndex = selectedMediaDataId
          ? orderedMediaItems.findIndex((item) => item.dataset.mediaId === selectedMediaDataId)
          : -1;
        const isDifferentVariantBoundary = (item) => {
          if (item.dataset.variantMedia !== 'true') return false;

          const itemVariantOptionToken = this.normalizeMediaToken(item.dataset.variantOptionValue);
          if (!selectedColorToken || !itemVariantOptionToken) return true;

          return itemVariantOptionToken !== selectedColorToken;
        };
        const nextVariantMediaIndex =
          selectedMediaIndex >= 0
            ? orderedMediaItems.findIndex((item, index) => index > selectedMediaIndex && isDifferentVariantBoundary(item))
            : -1;
        const groupedItems =
          selectedMediaIndex >= 0
            ? orderedMediaItems.slice(selectedMediaIndex, nextVariantMediaIndex > -1 ? nextVariantMediaIndex : undefined)
            : [];

        const colorMatchedItems = mediaItems.filter((item) => {
          const image = item.querySelector('img');
          const mediaToken = this.normalizeMediaToken(
            [
              image?.alt,
              image?.getAttribute('src'),
              image?.currentSrc,
              image?.getAttribute('srcset'),
              item.dataset.mediaId,
            ].join(' ')
          );
          const isSelectedFeaturedMedia =
            selectedMediaId && item.dataset.mediaId && item.dataset.mediaId.endsWith(`-${selectedMediaId}`);

          return (selectedColorToken && mediaToken.includes(selectedColorToken)) || isSelectedFeaturedMedia;
        });

        const visibleItems = groupedItems.length
          ? groupedItems
          : colorMatchedItems.length
            ? colorMatchedItems
          : selectedMediaId
            ? mediaItems.filter((item) => item.dataset.mediaId?.endsWith(`-${selectedMediaId}`))
            : [mediaItems[0]];
        const visibleMediaIds = new Set(visibleItems.map((item) => item.dataset.mediaId));

        const activeItem = mediaItems.find((item) => item.classList.contains('is-active'));
        const nextActiveItem = visibleItems[0];
        if (nextActiveItem && (!activeItem || !visibleMediaIds.has(activeItem.dataset.mediaId))) {
          this.setActiveMedia(nextActiveItem.dataset.mediaId, false);
        }

        mediaItems.forEach((item) => {
          item.classList.toggle('variant-media-hidden', !visibleMediaIds.has(item.dataset.mediaId));
        });

        thumbnailItems.forEach((item) => {
          item.classList.toggle('variant-media-hidden', !visibleMediaIds.has(item.dataset.target));
        });
      }
    }
  );
}
